// GeometryCache
// Library entries are keyed by `hash` alone (LOD is an index, not a key).
// A library entry is a 3-element array — one slot per LOD level. Each
// slot carries { geometry, material, name } extracted from the
// corresponding mesh in the fragment GLB.
//
// Materialized InstancedMesh objects are keyed by `${tileKey}::${hash}`.
// The current LOD is tracked by StreamingController on the InstancedMesh
// itself (instanced.userData.currentLod), not on the cache, because the
// same InstancedMesh swaps geometry pointers in place — the cache entry
// doesn't change identity when the LOD changes.
//
// === LRU eviction ===
//
// Both maps track `lastAccess` timestamps and have eviction methods
// (`evictInstanced`, `evictLibrary`) that StreamingController calls
// after each tick. Two separate caps because the maps have different
// "in use" definitions and different per-entry costs:
//
//   - instancedEntries: each entry holds an InstancedMesh with its own
//     GPU buffers (instanceMatrix, instanceColor). Heavier per-entry.
//     "In use" = currently parented in the scene graph.
//   - libraryEntries: each entry holds shared BufferGeometry + material
//     across 3 LODs. Lighter per-entry, but pinned by every InstancedMesh
//     that references it. "In use" = at least one instancedEntry hash
//     matches OR the mesh.parent reference chain stays alive.
//
// Eviction order matters: instanced first, then library. If we evict
// library while an InstancedMesh still references the geometry, the
// mesh has a dangling pointer. By evicting instanced first, we release
// references; then library entries that lost their last reference
// become eligible for library eviction next pass.

// We avoid importing Three to keep this module dependency-light. The
// only Three-specific bits we touch are .dispose() calls on geometry,
// material, instanceMatrix, instanceColor, and the InstancedMesh itself
// — all duck-typed via `typeof obj.dispose === 'function'`.

import thrashLedger from './thrashLedger';

export default class GeometryCache {
  // `ledger`: where evictions and reloads are counted (thrashLedger.js);
  // the module ledger perfSnapshot reads unless a test hands its own.
  constructor({ ledger = thrashLedger } = {}) {
    this.ledger = ledger;
    // hash -> { libraryParts, lastAccess }
    //   libraryParts = [LodSlot, LodSlot, LodSlot]
    //   LodSlot = { geometry, material, name } | null
    this.libraryEntries = new Map();

    // `${tileKey}::${hash}` -> { fragments, records, lastAccess, hash, tileKey }
    //   fragments = InstancedMesh[]
    //   records   = Map<uniqueId, ...>
    //   hash      = denormalized for fast library-reverse-lookup during eviction
    //   tileKey   = denormalized so eviction can ask "is this tile still visible"
    this.instancedEntries = new Map();

    // LRU order is all `lastAccess` is used for (the evictions sort by it),
    // so it is a counter, not a clock (round 11). getLibrary is asked for
    // every group of every tile by the planning passes - Tom's r10 profiles
    // put 1.1-1.3 s of self time in it, a Map lookup and a performance.now()
    // per call - and the counter keeps exactly the same order for a fraction
    // of the cost.
    this._accessSeq = 0;
  }

  _touch() { this._accessSeq += 1; return this._accessSeq; }

  static instKey(tileKey, hash) { return `${tileKey}::${hash}`; }

  hasLibrary(hash) { return this.libraryEntries.has(hash); }

  // `way`: 'fetch' or 'prefill' (a pack filling its other fragments) -
  // only for the ledger, which counts a new entry for an evicted hash as a
  // reload.
  putLibrary(hash, libraryParts, way = 'fetch') {
    // libraryParts must be a 3-element array (one slot per LOD), null
    // entries allowed if a LOD level was simplified out of existence.
    const prev = this.libraryEntries.get(hash);
    if (prev) this.libraryBytes = Math.max(0, (this.libraryBytes || 0) - (prev.bytes || 0));
    else if (this.ledger) this.ledger.libraryPut(hash, way);
    const bytes = GeometryCache.bytesOf(libraryParts);
    this.libraryEntries.set(hash, {
      libraryParts,
      bytes,
      lastAccess: this._touch(),
    });
    this.libraryBytes = (this.libraryBytes || 0) + bytes;
  }

  // GPU bytes of a library entry: every LOD slot's geometry attributes
  // and index. Feeds the byte budget (deviceMemory-scaled) that bounds
  // what a big model keeps resident.
  static bytesOf(libraryParts) {
    let bytes = 0;
    for (const slot of (libraryParts || [])) {
      const g = slot && slot.geometry;
      if (!g) continue;
      if (g.index && g.index.array) bytes += g.index.array.byteLength;
      for (const k of Object.keys(g.attributes || {})) {
        const a = g.attributes[k];
        if (a && a.array) bytes += a.array.byteLength;
      }
    }
    return bytes;
  }

  getLibrary(hash) {
    const e = this.libraryEntries.get(hash);
    if (!e) return null;
    // Bump lastAccess on every read — that's what makes eviction LRU.
    e.lastAccess = this._touch();
    return e.libraryParts;
  }

  putInstanced(tileKey, hash, payload) {
    const key = GeometryCache.instKey(tileKey, hash);
    this.instancedEntries.set(key, {
      ...payload,
      hash,
      tileKey,
      lastAccess: this._touch(),
    });
  }

  getInstanced(tileKey, hash) {
    const e = this.instancedEntries.get(GeometryCache.instKey(tileKey, hash));
    if (!e) return null;
    e.lastAccess = this._touch();
    return e;
  }

  markInstancedDisposed(tileKey, hash) {
    this.instancedEntries.delete(GeometryCache.instKey(tileKey, hash));
  }

  // ============================================================
  // LRU eviction — called from StreamingController.tick after the
  // load/unload pass. Both methods are idempotent and cheap when
  // under cap.
  // ============================================================

  /**
   * Evict instancedEntries down to `maxEntries`. The `isInUse`
   * predicate is called per-entry; entries that return true are
   * skipped (never evicted). Typical predicate from caller:
   *
   *   (entry) => entry.fragments.some(f => f.parent !== null)
   *
   * which says "if any of the fragments is still parented to something,
   * it's rendering — don't dispose it."
   *
   * Returns count of evicted entries.
   */
  evictInstanced(maxEntries, isInUse) {
    if (this.instancedEntries.size <= maxEntries) return 0;

    const candidates = [];
    this.instancedEntries.forEach((entry, key) => {
      if (isInUse && isInUse(entry)) return;
      candidates.push({ key, entry });
    });

    if (candidates.length === 0) return 0;

    candidates.sort((a, b) => a.entry.lastAccess - b.entry.lastAccess);

    const toDrop = Math.min(
      candidates.length,
      this.instancedEntries.size - maxEntries
    );

    let evicted = 0;
    for (let i = 0; i < toDrop; i++) {
      const { key, entry } = candidates[i];
      this._disposeInstancedPayload(entry);
      this.instancedEntries.delete(key);
      evicted++;
    }
    if (this.ledger) this.ledger.instancedEvicted(evicted);
    return evicted;
  }

  /**
   * Evict libraryEntries down to `maxEntries`. An entry is eligible
   * only if no instancedEntries currently references its hash —
   * otherwise we'd dangle the InstancedMesh's geometry pointer.
   *
   * Returns count of evicted entries.
   */
  // Evict least-recently-used libraries while over maxEntries OR over
  // maxBytes. `isInUse(hash)` pins libraries whose geometry a live pool
  // draws: pooled mode keeps nothing in instancedEntries, so without the
  // predicate an eviction could dispose geometry that is on screen.
  evictLibrary(maxEntries, maxBytes = Infinity, isInUse = null) {
    const bytes = this.libraryBytes || 0;
    if (this.libraryEntries.size <= maxEntries && bytes <= maxBytes) return 0;

    const pinned = new Set();
    this.instancedEntries.forEach(entry => pinned.add(entry.hash));

    const candidates = [];
    this.libraryEntries.forEach((entry, hash) => {
      if (pinned.has(hash)) return;
      if (isInUse && isInUse(hash)) return;
      candidates.push({ hash, entry });
    });
    if (candidates.length === 0) return 0;

    candidates.sort((a, b) => a.entry.lastAccess - b.entry.lastAccess);

    let evicted = 0;
    let size = this.libraryEntries.size;
    let over = bytes;
    for (const { hash, entry } of candidates) {
      if (size <= maxEntries && over <= maxBytes) break;
      // Which cap this one went for: the entry cap while the count is over
      // it, else the byte cap.
      if (this.ledger) this.ledger.libraryEvicted(hash, entry.bytes || 0, size > maxEntries ? 'entryCap' : 'byteCap');
      this.disposeLibrary(hash);
      evicted++;
      size--;
      over -= entry.bytes || 0;
    }
    return evicted;
  }

  // ============================================================
  // Disposal helpers
  // ============================================================

  /**
   * Dispose the GPU buffers and the InstancedMesh itself for a single
   * cached payload. Materials are NOT disposed here — they may be
   * shared via three.js's standard cache, and if we double-free we
   * crash the next render. Library disposal handles geometries.
   */
  _disposeInstancedPayload(payload) {
    if (!payload || !Array.isArray(payload.fragments)) return;

    for (const mesh of payload.fragments) {
      if (!mesh) continue;

      // Defensive: if the mesh is somehow still in the scene graph,
      // detach it. Shouldn't happen in normal flow because _unload
      // removes from scene before adapter.unregister, but cheap
      // safety net.
      if (mesh.parent && typeof mesh.parent.remove === 'function') {
        mesh.parent.remove(mesh);
      }

      // Dispose the per-instance attribute buffers. These are owned
      // by THIS InstancedMesh (not shared), so safe to dispose
      // unconditionally.
      if (mesh.instanceMatrix && typeof mesh.instanceMatrix.dispose === 'function') {
        mesh.instanceMatrix.dispose();
      }
      if (mesh.instanceColor && typeof mesh.instanceColor.dispose === 'function') {
        mesh.instanceColor.dispose();
      }

      // Dispose the InstancedMesh itself. In recent three.js this
      // releases internal renderer state. Older versions don't have
      // dispose() on Object3D, so guard.
      if (typeof mesh.dispose === 'function') {
        mesh.dispose();
      }
    }
  }

  // Heavy-hammer release for memory pressure scenarios. Disposes
  // BufferGeometries across all 3 LODs for a hash; materials are
  // shared with three.js's standard material cache so we leave those
  // alone unless the caller knows what they're doing.
  //
  // Public — callable directly for emergency memory reclaim.
  // Also called internally by evictLibrary.
  disposeLibrary(hash) {
    const prevEntry = this.libraryEntries.get(hash);
    if (prevEntry) this.libraryBytes = Math.max(0, (this.libraryBytes || 0) - (prevEntry.bytes || 0));
    const e = this.libraryEntries.get(hash);
    if (!e) return;
    e.libraryParts.forEach(slot => {
      if (slot && slot.geometry && typeof slot.geometry.dispose === 'function') {
        slot.geometry.dispose();
      }
    });
    this.libraryEntries.delete(hash);
  }

  clear() {
    // Best-effort full disposal. Walk every cached entry and free GPU
    // resources before dropping the maps. Used on viewer teardown.
    this.instancedEntries.forEach(payload => this._disposeInstancedPayload(payload));
    this.instancedEntries.clear();

    this.libraryEntries.forEach((entry, hash) => {
      entry.libraryParts.forEach(slot => {
        if (slot && slot.geometry && typeof slot.geometry.dispose === 'function') {
          slot.geometry.dispose();
        }
      });
    });
    this.libraryEntries.clear();
  }

  stats() {
    let fullyDistinct = 0;
    let padded = 0;
    this.libraryEntries.forEach(({ libraryParts }) => {
      const refs = new Set(libraryParts.map(p => p ? p.geometry : null));
      if (refs.size === libraryParts.length) fullyDistinct++;
      else padded++;
    });

    const wrappersPerHash = new Map();
    this.instancedEntries.forEach((_payload, key) => {
      const sep = key.lastIndexOf('::');
      const hash = sep >= 0 ? key.slice(sep + 2) : key;
      wrappersPerHash.set(hash, (wrappersPerHash.get(hash) ?? 0) + 1);
    });

    let maxSharingFactor = 0;
    let totalWrappers = 0;
    wrappersPerHash.forEach(count => {
      if (count > maxSharingFactor) maxSharingFactor = count;
      totalWrappers += count;
    });
    const avgSharingFactor = wrappersPerHash.size > 0
      ? totalWrappers / wrappersPerHash.size
      : 0;

    return {
      hashesCached:           this.libraryEntries.size,
      hashesFullyDistinctLod: fullyDistinct,
      hashesPaddedLod:        padded,
      instancedWrappersCached: this.instancedEntries.size,
      uniqueHashesWithWrappers: wrappersPerHash.size,
      maxSharingFactor,
      avgSharingFactor: +avgSharingFactor.toFixed(2),
    };
  }
}